import { Component, Inject, OnInit, Optional, ViewChild, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTreeModule, MatTree } from '@angular/material/tree';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  RimsObject,
  RimsStep,
  RimsStepSchema,
  RimsStepProperty,
  RimsStepPropertySchema,
  RimsStepPropertyTree,
} from '@app/models/rims.model';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  RimsFormBuilderService,
  RimsFormDescriptor,
} from '@app/pages/incidents/details-page/rims/rims-edit-panel/rims-form/rims-form-builder/rims-form-builder.service';
import { IncidentDetailsPageStore } from '@app/pages/incidents/details-page/incident-details-page.store';
import { formatAsDateOnlyString } from '@app/utils/datetime/datetime.utils';
import {
  createRimsStepProperty,
  getPropertyType,
  isPropertyTypeObjectOrArray,
  isRimsSetItemField,
  updateRimsStepFromForms,
} from '@app/utils/rims/rims.utils';
import { ConfirmationModalComponent } from '../../../../../components/confirmation-modal/confirmation-modal.component';
import { BaseModalComponent } from '../../../../../components/base-modal/base-modal.component';
import { RimsFormComponent } from './rims-form/rims-form.component';
import { FieldReferenceService } from '../../field-references.service';

@Component({
  standalone: true,
  selector: 'app-rims-edit-panel',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatTreeModule,
    MatTooltipModule,
    RimsFormComponent,
    BaseModalComponent,
  ],
  templateUrl: './rims-step-edit-modal.component.html',
  styleUrl: './rims-step-edit-modal.component.scss',
})
export class RimsStepEditModalComponent implements OnInit {
  @ViewChild(MatTree) private tree?: MatTree<RimsStepPropertyTree, RimsStepPropertyTree>;

  protected readonly getPropertyType = getPropertyType;
  protected readonly isPropertyTypeObjectOrArray = isPropertyTypeObjectOrArray;
  protected readonly activeFormGroup = computed(() => {
    const activeForm = this.formBuilderService.activeForm();
    return activeForm?.formGroup;
  });
  protected selectedRimsStepPropertyTree?: RimsStepPropertyTree;
  protected dataSource$ = new BehaviorSubject<RimsStepPropertyTree[]>([]);
  protected childrenAccessor = (node: RimsStepPropertyTree) => node.children?.filter((child) => child.showInTree) ?? [];
  protected hasChild = (_: number, node: RimsStepPropertyTree) =>
    !!node.children?.filter((child) => child.showInTree).length;

  constructor(
    private readonly dialogRef: MatDialogRef<RimsStepEditModalComponent>,
    private readonly dialog: MatDialog,
    protected readonly formBuilderService: RimsFormBuilderService,
    @Optional() protected readonly incidentsPageStore?: IncidentDetailsPageStore, // This is marked as Optional() as this component is also used to display the Rims structure in the RimsPage
    @Optional() protected readonly fieldReferenceService?: FieldReferenceService, // This is marked as Optional() as this service depends on the store
    @Inject(MAT_DIALOG_DATA) public data?: { rimsStep: RimsStep; rimsStepSchema: RimsStepSchema },
  ) {}

  async ngOnInit(): Promise<void> {
    this.generateTreeStructure();
    // Pre-select the first item in the tree
    await this.onRimsStepPropertyTreeSelectedAsync(this.dataSource$.value[0]);
  }

  protected get getSetEntries(): RimsObject[] {
    return this.selectedRimsStepPropertyTree?.value as RimsObject[];
  }

  protected nodeAsRimsFieldsTreeType(node: RimsStepPropertyTree): RimsStepPropertyTree {
    return node as RimsStepPropertyTree;
  }

  protected async onRimsStepPropertyTreeSelectedAsync(field: RimsStepPropertyTree): Promise<void> {
    this.selectedRimsStepPropertyTree = field;

    this.tree?.expand(this.selectedRimsStepPropertyTree);
    await this.setActiveFormAsync();
  }

  protected getRimsStepPropertyTreesBasedOnPropertyType(): RimsStepPropertyTree[] {
    if (!this.selectedRimsStepPropertyTree) {
      return [];
    }

    switch (getPropertyType(this.selectedRimsStepPropertyTree.schema)) {
      case 'object':
        return this.selectedRimsStepPropertyTree.children!.filter(
          (child) => !isRimsSetItemField(child), // Exclude the "id" of set items (RimsSetItem.Id)
        );
      case 'array':
        return [];
      default:
        return [this.selectedRimsStepPropertyTree];
    }
  }

  protected getRimsStepPropertyTreeDisplayValue(node: RimsStepPropertyTree): RimsStepProperty {
    // Find Enum display value
    if (node.schema.enum !== undefined) {
      const enumValue = node.schema.enum.find((e) => e.value === node.value);
      return enumValue ? enumValue.displayValue : node.value;
    }

    if (node.schema.format === 'date') {
      return formatAsDateOnlyString(node.value as string | null);
    }

    return node.value;
  }

  protected async addSetEntry(): Promise<void> {
    const newSetItem = createRimsStepProperty(this.selectedRimsStepPropertyTree!.schema.items!, false);
    if (!newSetItem) {
      return;
    }

    // Add item
    (this.selectedRimsStepPropertyTree?.value as RimsObject[]).push(newSetItem as RimsObject);

    // Regenerate tree to reflect changes
    this.generateTreeStructure();

    // After we refresh the tree we need to highlight the set item again
    const treeFields = this.dataSource$.value;
    const set = treeFields.find((item) => getPropertyType(item.schema) === 'array');
    if (set) {
      this.tree?.expand(set);
      // Mark the set as the selected item again because the tree is regenerated and the current item no longer valid
      await this.onRimsStepPropertyTreeSelectedAsync(set);
    }
  }

  protected async removeSetEntry(atIndex: number): Promise<void> {
    // Confirm
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        title: 'Warning',
        content: `Entry ${atIndex + 1} will be deleted.`,
      },
    });
    const dialogResult = await firstValueFrom(dialogRef.afterClosed());
    if (dialogResult == false) {
      return;
    }

    // Update the field reference service to mark the set item as removed
    const setMarkedForDelete = (this.selectedRimsStepPropertyTree?.value as RimsObject[])[atIndex];
    const stepId = this.data?.rimsStep?.$type;
    const setId = setMarkedForDelete['id'];
    if (stepId && setId) {
      this.fieldReferenceService?.removeTechnicalStepSetItemFieldReference(stepId, setId.toString());
    }

    // Remove item
    (this.selectedRimsStepPropertyTree?.value as RimsObject[]).splice(atIndex, 1);

    // Regenerate tree to reflect changes
    this.generateTreeStructure();

    // After we refresh the tree we need to highlight the set item again
    const treeFields = this.dataSource$.value;
    const set = treeFields.find((item) => getPropertyType(item.schema) === 'array');
    if (set) {
      this.tree?.expand(set);
      // Mark the set as the selected item again because the tree is regenerated and the current item no longer valid
      await this.onRimsStepPropertyTreeSelectedAsync(set);
    }
  }

  protected onUpdate(forms: RimsFormDescriptor[] | undefined): void {
    if (!forms || !Array.isArray(forms)) {
      return;
    }

    updateRimsStepFromForms(this.data!.rimsStep, forms);

    this.incidentsPageStore!.isRimsEdited = true;
    this.formBuilderService.deregisterAllForms();
    this.dialogRef.close();
  }

  protected onClose(): void {
    this.dialogRef.close();
  }

  private generateTreeStructure(): void {
    if (!this.data) {
      return;
    }

    const generateChildLeaf = (
      name: string,
      rimsStepProperty: RimsStepProperty,
      rimsStepPropertySchema: RimsStepPropertySchema,
      objectPath: string,
      parent: { container: unknown; key: string | number },
      displayName?: string,
    ): RimsStepPropertyTree => {
      const result: RimsStepPropertyTree = {
        name: name,
        displayName: displayName ?? rimsStepPropertySchema.displayName ?? name,
        description: rimsStepPropertySchema.description,
        value: rimsStepProperty,
        schema: rimsStepPropertySchema,
        objectPath,
        showInTree: true,
      };

      const propertyType = getPropertyType(rimsStepPropertySchema);
      if (propertyType === 'array') {
        result.children = (rimsStepProperty as RimsObject[]).map((item, index) => {
          return generateChildLeaf(
            `${result.displayName} [${index + 1}]`,
            item,
            rimsStepPropertySchema.items!,
            `${objectPath}/${index}`,
            { container: rimsStepProperty, key: index },
            `${result.displayName} [${index + 1}]`,
          );
        });
      } else if (propertyType === 'object') {
        // Objects that come as null from database must be initialized in the UI
        if (rimsStepProperty === null) {
          rimsStepProperty = createRimsStepProperty(rimsStepPropertySchema, false);
          result.value = rimsStepProperty;
          // Write it back to the model, otherwise edits to its children have no existing path to be saved to
          (parent.container as Record<string | number, RimsStepProperty>)[parent.key] = rimsStepProperty;
        }
        result.children = Object.entries(rimsStepProperty as RimsObject).map(([childName, childValue]) => {
          const childSchema = rimsStepPropertySchema.properties![childName];
          const childNode = generateChildLeaf(childName, childValue, childSchema, `${objectPath}/${childName}`, {
            container: rimsStepProperty,
            key: childName,
          });

          if (!isPropertyTypeObjectOrArray(childSchema)) {
            childNode.showInTree = false;
          }

          return childNode;
        });
      }

      return result;
    };

    const treeData = Object.entries(this.data.rimsStep)
      .filter(([name]) => name !== '$type')
      .map(([name, value]) => {
        const rimsStepPropertySchema = this.data!.rimsStepSchema.properties[name];
        return generateChildLeaf(name, value, rimsStepPropertySchema, name, {
          container: this.data!.rimsStep,
          key: name,
        });
      });

    this.dataSource$.next(treeData);
  }

  private async setActiveFormAsync(): Promise<void> {
    if (this.selectedRimsStepPropertyTree === undefined) {
      return;
    }

    // Collections don't have a form, the Objects inside them do
    if (getPropertyType(this.selectedRimsStepPropertyTree.schema) === 'array') {
      this.formBuilderService.clearActiveForm();
    } else if (!this.formBuilderService.formExists(this.selectedRimsStepPropertyTree)) {
      const formDescriptor = await this.formBuilderService.buildForm(this.selectedRimsStepPropertyTree);
      // Fixes issue with for not updating when first opening the modal
      this.formBuilderService.setActiveForm(formDescriptor);
    } else {
      const formId = this.formBuilderService.getFormId(this.selectedRimsStepPropertyTree);
      // Set the active form
      this.formBuilderService.setActiveFormById(formId!);
    }
  }
}
